import type { Optional } from 'utility-types'
import React, { createRef, type FC, useState } from 'react'
import { Box, type SxProps, type Theme, Typography } from '@mui/material'

import { Translation } from '../../../../domain'

import { createClickerHandler } from '../..'
import { FormattedTranslation } from '../../Internationalization'
import { Button } from '../Button'
import { HiddenRawFileUpload, type HiddenRawFileUploadProps } from './Raw'

const wrapperStyle: SxProps<Theme> = { display: 'flex', overflow: 'hidden', '& > *': { my: 'auto' } }
const buttonStyle: SxProps<Theme> = { px: 4, mr: 2, minWidth: 120, overflow: 'hidden' }
const labelStyle: SxProps<Theme> = { textOverflow: 'ellipsis', overflow: 'hidden', display: 'flex', alignItems: 'center' }

type Props =
    & Readonly<{
        /**
         * Does not really loads the files, but pretends to with the label
         */
        initialValue?: readonly File[]
    }>
    & Optional<HiddenRawFileUploadProps, 'onChange'>

/**
 * Just a localized material UI implementation of `<input type="file" />`
 */
export const FileUpload: FC<Props> = ({ initialValue = [], onChange, readOnly, ...props }) => {
    const ref = createRef<HTMLInputElement>()
    const [{ length, 0: firstFile }, setFakeValue] = useState(() => initialValue)

    return (
        <Box sx={wrapperStyle}>
            <Button sx={buttonStyle} variant="primaryOutline" size="large" disabled={readOnly} onClick={createClickerHandler(ref)}>
                <FormattedTranslation id={Translation.FILE_UPLOAD_LABEL} />
            </Button>

            <Typography sx={labelStyle} variant="body2">
                <FormattedTranslation id={Translation.FILE_UPLOAD} values={{ length, firstFileName: firstFile?.name }} />
            </Typography>

            <HiddenRawFileUpload
                {...props}
                readOnly={readOnly}
                ref={ref}
                onChange={(files) => {
                    setFakeValue(files)
                    onChange?.(files)
                }}
            />
        </Box>
    )
}
